import { useCallback, useState } from 'react';

/**
 * Which production method the saw at this station cuts for.
 *
 * A station's method is configuration, not a filter. Production asked for it to
 * be set once per tablet and then left alone, the way the building is: a saw
 * dedicated to Swiss does not switch to Fastlane between cuts, so offering the
 * choice on the filter row made an everyday control out of a decision that is
 * made when the station is set up. It also cost a third of that row, which on a
 * 10.1" screen is list the operator needs more.
 *
 * Stored per device rather than against the station record, which is what the
 * building does. That means it survives a restart but not a cleared browser,
 * and anyone at the tablet can change it — the same trade the building makes.
 */
export const PRODUCTION_METHOD_SCOPES = ['All', 'Fastlane', 'Swiss'] as const;

export type ProductionMethodScope = (typeof PRODUCTION_METHOD_SCOPES)[number];

/**
 * `All` rather than a method, for two reasons. A shop running one saw for both
 * methods has to see both, and until every saw is confirmed dedicated a default
 * of `Fastlane` would silently hide every Swiss cut. And a station that never
 * opens the picker keeps the queue it had before this setting existed, so
 * shipping it changes nothing until someone chooses.
 */
export const DEFAULT_PRODUCTION_METHOD_SCOPE: ProductionMethodScope = 'All';

/**
 * Cutting's key. Kitting uses a different one: the same iPad often opens both
 * tiles, and a Swiss saw setting must not hide Fastlane kits (or the reverse).
 */
export const PRODUCTION_METHOD_STORAGE_KEY = 'fastlane_tablet_production_method';
export const KITTING_PRODUCTION_METHOD_STORAGE_KEY =
	'fastlane_tablet_kitting_production_method';

const isScope = (value: string | null): value is ProductionMethodScope =>
	value !== null &&
	(PRODUCTION_METHOD_SCOPES as readonly string[]).includes(value);

/**
 * `localStorage` is not always reachable: a sandboxed iframe or a browser with
 * site data blocked throws `SecurityError` on access rather than returning
 * null. Unguarded in the state initialiser below that throw escapes during
 * render and blanks the page, which trades a remembered setting for the whole
 * queue. Losing the setting is the smaller failure, so it is the one taken.
 */
const readStoredScope = (storageKey: string): string | null => {
	try {
		return localStorage.getItem(storageKey);
	} catch {
		return null;
	}
};

const writeStoredScope = (
	storageKey: string,
	value: ProductionMethodScope
): void => {
	try {
		localStorage.setItem(storageKey, value);
	} catch {
		// The choice still applies to this session; it just will not survive a
		// reload. Nothing to tell the operator, who did not ask for it to persist.
	}
};

export function usePersistedProductionMethod(
	storageKey: string = PRODUCTION_METHOD_STORAGE_KEY
) {
	const [scope, setScopeState] = useState<ProductionMethodScope>(() => {
		const stored = readStoredScope(storageKey);
		// A value we do not recognise falls back to the default instead of
		// reaching the API. An unknown method matches no work, and an empty saw
		// queue reads to an operator as "nothing to cut" rather than as a broken
		// setting, so they would stand at an idle saw and believe it.
		return isScope(stored) ? stored : DEFAULT_PRODUCTION_METHOD_SCOPE;
	});

	const setScope = useCallback(
		(next: ProductionMethodScope) => {
			writeStoredScope(storageKey, next);
			setScopeState(next);
		},
		[storageKey]
	);

	return { scope, setScope };
}

/**
 * The value the grouped-reservations endpoint expects. `All` is the absence of
 * the filter, not a value it understands.
 */
export const productionMethodParam = (scope: ProductionMethodScope): string =>
	scope === 'All' ? '' : scope;
